import React, { useEffect, useState } from 'react';
import { Redirect } from 'react-router';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '@shared/store/auth.duck';
import { agent, getErrorMessage } from '@shared/services/agent';
import { getParameterByName } from '@shared/utils/UrlUtils';
import AuthWrapper from '@features/auth/pages/auth/components/AuthWrapper';
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { BodyRegular, BodySmall } from '@shared/ui/atoms/fonts/Body';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';

/**
 * OAuth consent screen for MCP clients (e.g. claude.ai). The authorization server redirects the
 * browser here (carrying the OAuth query params); the user — signed in with their normal TeamToday
 * session — approves, and we hand the request to the backend which mints the authorization code and
 * returns the client's redirect URL.
 */
export default function McpConsentPage() {
  const currentUser = useSelector(selectCurrentUser);

  const clientId = getParameterByName('client_id', window.location.href) || '';
  const redirectUri = getParameterByName('redirect_uri', window.location.href) || '';
  const codeChallenge = getParameterByName('code_challenge', window.location.href) || '';
  const codeChallengeMethod = getParameterByName('code_challenge_method', window.location.href) || '';
  const state = getParameterByName('state', window.location.href) || '';
  const scope = getParameterByName('scope', window.location.href) || '';
  const responseType = getParameterByName('response_type', window.location.href) || '';

  const [clientName, setClientName] = useState<string>('');
  const [mcpEnabled, setMcpEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!currentUser || !clientId || !redirectUri) return;
    // The backend validates client_id + redirect_uri here; a failure means we must NOT render the
    // redirecting Allow/Deny buttons (an unvalidated redirect_uri would be an open redirect on Deny).
    agent.requests
      .get(`/api/mcp-oauth/authorization-request?client_id=${encodeURIComponent(clientId)}&redirect_uri=${encodeURIComponent(redirectUri)}`)
      .then((res: { clientName?: string; companyMcpEnabled?: boolean }) => {
        setClientName(res?.clientName || 'An application');
        setMcpEnabled(res?.companyMcpEnabled !== false);
      })
      .catch(() => setLoadError('This authorization request is invalid or has expired. Please start again from the application you were connecting.'))
      .finally(() => setLoading(false));
  }, [currentUser, clientId, redirectUri]);

  // The user must be signed in to approve. (Return-to-consent after a fresh login is a follow-up;
  // in the normal flow the admin connecting Claude is already signed in.)
  if (!currentUser) {
    return <Redirect to="/login" />;
  }

  if (!clientId || !redirectUri) {
    return (
      <AuthWrapper dataTest="mcp-consent-invalid" title="Authorization" hideLoginLink={true}>
        <BodyRegular>This authorization request is missing required parameters.</BodyRegular>
      </AuthWrapper>
    );
  }

  const handleAllow = async () => {
    setSubmitting(true);
    setError('');
    try {
      const res = await agent.requests.post('/api/mcp-oauth/approve', {
        clientId,
        redirectUri,
        scope,
        state,
        codeChallenge,
        codeChallengeMethod,
        responseType,
      });
      window.location.assign(res.redirectUri);
    } catch (e) {
      setSubmitting(false);
      setError(getErrorMessage(e, 'Something went wrong approving access. Please try again.'));
    }
  };

  const handleDeny = () => {
    const sep = redirectUri.includes('?') ? '&' : '?';
    let denyUri = `${redirectUri}${sep}error=access_denied`;
    if (state) denyUri += `&state=${encodeURIComponent(state)}`;
    window.location.assign(denyUri);
  };

  if (loading) {
    return <AuthWrapper dataTest="mcp-consent-loading" title="Authorization" hideLoginLink={true} spinner={true} />;
  }

  // Load failed / request not validated by the backend: terminal screen with no redirecting buttons.
  if (loadError) {
    return (
      <AuthWrapper dataTest="mcp-consent-error" title="Authorization" hideLoginLink={true}>
        <BodyRegular>{loadError}</BodyRegular>
      </AuthWrapper>
    );
  }

  if (!mcpEnabled) {
    return (
      <AuthWrapper dataTest="mcp-consent-disabled" title="Authorize access" hideLoginLink={true}>
        <BodyRegular style={{ marginBottom: 16 }}>
          MCP integration isn't enabled for your company.
        </BodyRegular>
        <BodySmall style={{ marginBottom: 24 }}>
          Ask a company admin to enable it under Settings &rarr; Other before connecting <strong>{clientName}</strong>.
        </BodySmall>
        <OutlineButton
          dataTest="mcp-consent-cancel"
          text="Cancel"
          fullWidth={true}
          size={ButtonSize.Large}
          click={handleDeny}
        />
      </AuthWrapper>
    );
  }

  return (
    <AuthWrapper dataTest="mcp-consent" title="Authorize access" hideLoginLink={true}>
      <BodyRegular style={{ marginBottom: 16 }}>
        <strong>{clientName}</strong> wants to access your TeamToday account as <strong>{currentUser.email}</strong>.
      </BodyRegular>
      <BodySmall style={{ marginBottom: 8 }}>
        It will be able to read your team's whereabouts and offices on your behalf.
      </BodySmall>
      {scope ? <BodySmall style={{ marginBottom: 24 }}>Requested scope: {scope}</BodySmall> : <div style={{ marginBottom: 24 }} />}

      {error ? <BodySmall style={{ marginBottom: 16, color: '#F0224F' }}>{error}</BodySmall> : null}

      <PrimaryButton
        dataTest="mcp-consent-allow"
        text="Allow access"
        fullWidth={true}
        size={ButtonSize.Large}
        disabled={submitting}
        click={handleAllow}
        style={{ marginBottom: 12 }}
      />
      <OutlineButton
        dataTest="mcp-consent-deny"
        text="Deny"
        fullWidth={true}
        size={ButtonSize.Large}
        disabled={submitting}
        click={handleDeny}
      />
    </AuthWrapper>
  );
}
